import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, CheckCircle2, Copy, Download, Eye, EyeOff, GripVertical, MessageSquareText, Plus, Redo2, RefreshCw, Sparkles, Trash2, Upload } from 'lucide-react'
import type { HybridReportDraft, HybridReportLibraryBlock, RatioAnalysisResponse, RatioCard, ServerApiClient } from '../../../data/ServerApiClient'
import { useAnalytics } from '../../../analytics'
import {
  balanceSheetRollupRows,
  cashFlowRollupRows,
  computeWaterfallBridge,
  profitAndLossRollupRows,
} from '../QuickBooksCfoPresentationExport'
import { buildRatioCardBlockModel, pinnedRatioCardSnapshot } from './presentation/ratioCardBlock'
import { applyHybridReportAiOperations } from './hybridReportAiOperations'
import './HybridExcelReportStudio.css'

type HybridModules = {
  normalizeHybridReportSpec: (value: any) => any
  hybridReportPatch: (spec: any, action: any) => any
  snapshotFromHybridReportSpec: (spec: any) => any
  hybridReportBlockCommentary: (spec: any, block: any) => any
  hybridReportCommentaryOverride: (spec: any, block: any, body: string, authorKind?: 'user' | 'ai') => any
  reportRowsPerPage: (section: any) => number
  buildReportHtml: (snapshot: any) => { html: string; title: string; warnings: string[] }
}

type Props = { appApi: ServerApiClient; workbookId: string; draftId: string; modulesOverride?: HybridModules }

const hybridSpecModuleUrl = '/excel-addin/hybrid-report-spec.js'
const reportHtmlModuleUrl = '/excel-addin/report-html-builder.js'

const safeId = (value: string) => value.toLowerCase().replace(/[^a-z0-9_.-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48)
const uniqueBlockId = (blocks: any[], seed: string) => {
  const base = safeId(seed) || 'report-block'
  let candidate = base
  let suffix = 2
  while (blocks.some((block) => block.id === candidate)) candidate = `${base.slice(0, 43)}-${suffix++}`
  return candidate
}
const money = (value: unknown) => typeof value === 'number'
  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  : String(value ?? '—')
const percentage = (value: unknown) => typeof value === 'number' && Number.isFinite(value)
  ? new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 }).format(value)
  : '—'
const metricAt = (metrics: any, path: string): number | null => {
  const value = path.split('.').reduce((current: any, key) => current?.[key], metrics)
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}
const monthKey = (value: unknown): string => {
  const source = String(value ?? '').trim()
  const iso = /^(\d{4})-(\d{2})/.exec(source)
  if (iso) return `${iso[1]}-${iso[2]}`
  const parsed = Date.parse(`1 ${source}`)
  return Number.isNaN(parsed) ? '' : new Date(parsed).toISOString().slice(0, 7)
}
const monthName = (value: unknown): string => {
  const key = monthKey(value)
  if (!key) return String(value ?? '').trim() || 'Not selected'
  return new Date(`${key}-01T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}
const dayName = (value: unknown): string => {
  const source = String(value ?? '').trim()
  if (!source) return 'Not available'
  const parsed = new Date(source)
  if (Number.isNaN(parsed.getTime())) return source
  return parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}
const timestampName = (value: unknown): string => {
  const parsed = new Date(String(value ?? ''))
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
const periodEndDayName = (value: unknown): string => {
  const key = monthKey(value)
  if (!key) return dayName(value)
  const [year, month] = key.split('-').map(Number)
  return new Date(Date.UTC(year, month, 0)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}
const monthShort = (value: unknown): string => {
  const key = monthKey(value)
  if (!key) return String(value ?? '')
  const date = new Date(`${key}-01T00:00:00Z`)
  return `${date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })}-${String(date.getUTCFullYear()).slice(-2)}`
}
export const effectiveHybridReportMonth = (spec: any): string => (
  /^\d{4}-\d{2}$/.test(String(spec?.report_month || ''))
    ? String(spec.report_month)
    : monthKey(spec?.document?.reportPeriodLabel)
)

export const hybridBlockHasEditableCommentary = (block: any): boolean => Boolean(
  block
  && block.kind !== 'cover_toc'
  && (
    block.kind === 'text'
    || block.content?.snapshot_section?.commentary
    || block.configuration?.commentary
  ),
)

const hybridChartSupportsWaterfall = (chart: any): boolean => Boolean(
  (Array.isArray(chart?.steps) && chart.steps.length)
  || (Array.isArray(chart?.values) && chart.values.length)
  || (Array.isArray(chart?.series) && chart.series.length === 1),
)

function statementThroughMonth(statement: any, reportMonth: string): any {
  const periods = Array.isArray(statement?.periods) ? statement.periods : []
  if (!reportMonth || !periods.length) return statement
  const indexes: Array<{ index: number; key: string }> = periods.map((period: unknown, index: number) => ({ index, key: monthKey(period) }))
  const end = indexes.filter((entry) => entry.key && entry.key <= reportMonth).at(-1)?.index
  if (end == null || end === periods.length - 1) return statement
  return {
    ...statement,
    periods: periods.slice(0, end + 1),
    rows: (statement?.rows || []).map((row: any) => ({
      ...row,
      values: Array.isArray(row?.values) ? row.values.slice(0, end + 1) : row?.values,
    })),
  }
}

function kpiCardsForSection(metrics: any, section: string): Array<{ label: string; value: string; detail?: string }> {
  if (section === 'balanceSheet') return [
    { label: 'Cash', value: money(metricAt(metrics, 'balanceSheet.cash')) },
    { label: 'Total assets', value: money(metricAt(metrics, 'balanceSheet.totalAssets')) },
    { label: 'Total liabilities', value: money(metricAt(metrics, 'balanceSheet.totalLiabilities')) },
    { label: 'Total equity', value: money(metricAt(metrics, 'balanceSheet.totalEquity')) },
  ]
  if (section === 'cashFlow') {
    const cashChange = metricAt(metrics, 'cashFlow.netCashChange')
    const endingCash = metricAt(metrics, 'cashFlow.endingCash')
    const revenue = metricAt(metrics, 'profitAndLoss.totalRevenue')
    return [
      { label: 'Net cash change', value: money(cashChange) },
      { label: 'Ending cash', value: money(endingCash) },
      { label: 'Revenue conversion', value: percentage(cashChange != null && revenue ? cashChange / revenue : null) },
      { label: 'Runway signal', value: endingCash == null ? 'N/A' : endingCash >= 0 ? 'Positive' : 'Deficit' },
    ]
  }
  const home = section === 'home'
  return [
    { label: home ? 'Total revenue' : 'Revenue', value: money(metricAt(metrics, 'profitAndLoss.totalRevenue')), detail: home ? 'Latest report month' : undefined },
    { label: home ? 'Net income' : 'Gross profit', value: money(metricAt(metrics, home ? 'profitAndLoss.netIncome' : 'profitAndLoss.grossProfit')), detail: home ? percentage(metricAt(metrics, 'profitAndLoss.netMarginPct')) : undefined },
    { label: 'EBITDA', value: money(metricAt(metrics, 'profitAndLoss.ebitda')), detail: home ? 'Analytical non-GAAP metric' : undefined },
    { label: 'Gross margin', value: percentage(metricAt(metrics, 'profitAndLoss.grossMarginPct')), detail: home ? 'Revenue quality' : undefined },
  ]
}

function rollupValue(rollup: { rows: Array<{ key: string; values: Array<number | null> }> }, key: string): number | null {
  const value = rollup.rows.find((row) => row.key === key)?.values.at(-1)
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function reportMonthMetrics(live: any, reportMonth: string): any {
  const statements = live?.statements || {}
  const profitAndLoss = profitAndLossRollupRows(
    statementThroughMonth(statements.profit_and_loss, reportMonth),
    24,
    live?.config?.derivedMetrics,
  )
  const balanceSheet = balanceSheetRollupRows(statementThroughMonth(statements.balance_sheet, reportMonth), 24)
  const cashFlow = cashFlowRollupRows(statementThroughMonth(statements.cash_flow, reportMonth), 24)
  const value = (rollup: any, key: string, livePath: string): number | null => {
    const governedValue = rollupValue(rollup, key)
    if (governedValue != null) return governedValue
    const liveMatchesReportMonth = !reportMonth || monthKey(live?.source_state?.reporting_period) === reportMonth
    return liveMatchesReportMonth ? metricAt(live?.metrics, livePath) : null
  }
  return {
    profitAndLoss: {
      totalRevenue: value(profitAndLoss, 'revenue', 'profitAndLoss.totalRevenue'),
      grossProfit: value(profitAndLoss, 'gross-profit', 'profitAndLoss.grossProfit'),
      netIncome: value(profitAndLoss, 'net-income', 'profitAndLoss.netIncome'),
      ebitda: value(profitAndLoss, 'ebitda', 'profitAndLoss.ebitda'),
      grossMarginPct: value(profitAndLoss, 'gross-margin', 'profitAndLoss.grossMarginPct'),
      netMarginPct: value(profitAndLoss, 'net-margin', 'profitAndLoss.netMarginPct'),
    },
    balanceSheet: {
      cash: value(balanceSheet, 'cash', 'balanceSheet.cash'),
      totalAssets: value(balanceSheet, 'assets', 'balanceSheet.totalAssets'),
      totalLiabilities: value(balanceSheet, 'liabilities', 'balanceSheet.totalLiabilities'),
      totalEquity: value(balanceSheet, 'equity', 'balanceSheet.totalEquity'),
    },
    cashFlow: {
      netCashChange: value(cashFlow, 'net-change', 'cashFlow.netCashChange'),
      endingCash: value(cashFlow, 'ending-cash', 'cashFlow.endingCash'),
    },
  }
}

function statementTrendChart(statement: any, rowLabel: string, reportMonth: string) {
  const bounded = statementThroughMonth(statement, reportMonth)
  const periods = Array.isArray(bounded?.periods) ? bounded.periods.slice(-12) : []
  const row = (bounded?.rows || []).find((candidate: any) => String(candidate?.label || candidate?.key || '').toLowerCase().includes(rowLabel.toLowerCase()))
  if (!row || !periods.length) return null
  const values = Array.isArray(row.values) ? row.values.slice(-periods.length).map((value: unknown) => Number(value) || 0) : []
  return {
    kind: 'bars',
    labels: periods.map((period: unknown) => monthName(period).replace(/ \d{4}$/, (year) => ` ${year.slice(-2)}`)),
    series: [{ label: row.label || rowLabel, type: 'bar', values }],
    value_format: 'currency',
  }
}

function quarterlyRevenueNetIncomeChart(statement: any, reportMonth: string) {
  const bounded = statementThroughMonth(statement, reportMonth)
  const rollup = profitAndLossRollupRows(bounded, 24)
  const rows = new Map(rollup.rows.map((row) => [row.key, row]))
  const quarters = new Map<string, { revenue: number; netIncome: number; ebitda: number }>()
  rollup.periods.forEach((period, index) => {
    const date = new Date(`${String(period).slice(0, 10)}T00:00:00Z`)
    if (Number.isNaN(date.getTime())) return
    const label = `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${String(date.getUTCFullYear()).slice(-2)}`
    const current = quarters.get(label) || { revenue: 0, netIncome: 0, ebitda: 0 }
    current.revenue += Number(rows.get('revenue')?.values[index]) || 0
    current.netIncome += Number(rows.get('net-income')?.values[index]) || 0
    current.ebitda += Number(rows.get('ebitda')?.values[index]) || 0
    quarters.set(label, current)
  })
  const entries = Array.from(quarters.entries()).slice(-8)
  if (!entries.length) return null
  return {
    kind: 'combo',
    labels: entries.map(([label]) => label),
    series: [
      { label: 'Revenue', type: 'bar', values: entries.map(([, value]) => value.revenue) },
      { label: 'Net income', type: 'line', values: entries.map(([, value]) => value.netIncome) },
      { label: 'EBITDA', type: 'line', values: entries.map(([, value]) => value.ebitda), tone: 'secondary' },
    ],
    value_format: 'currency',
  }
}

function ratioById(ratios: RatioAnalysisResponse | null, id: string): RatioCard | null {
  for (const group of ratios?.categories || []) {
    const card = group.ratios?.find((candidate) => candidate.id === id)
    if (card) return card
  }
  return null
}

function sourceConfig(item?: HybridReportLibraryBlock): Record<string, any> {
  return item?.source_config && typeof item.source_config === 'object' ? item.source_config : {}
}

export function libraryItemAvailable(item: HybridReportLibraryBlock, live: any, ratios: RatioAnalysisResponse | null, spec: any): boolean {
  if (item.kind === 'saved_view' || item.kind === 'text') return true
  try {
    const content: any = governedLibraryBlockContent(item.kind, live, ratios, item, spec)
    if (content?.ratio_card?.card) return true
    if (Array.isArray(content?.cards)) {
      return content.cards.some((card: any) => !['', '—', '–', 'N/A'].includes(String(card?.value || '').trim()))
    }
    if (Array.isArray(content?.matrix)) return content.matrix.length > 1
    const chart: any = content?.chart
    if (Array.isArray(chart?.steps)) return chart.steps.length > 0
    if (Array.isArray(chart?.series)) return chart.labels?.length > 0 && chart.series.some((series: any) => series.values?.length === chart.labels.length)
    if (Array.isArray(chart?.values)) return chart.labels?.length > 0 && chart.values.length === chart.labels.length
    return Boolean(String(content?.text || '').trim())
  } catch {
    return false
  }
}

async function sha256Json(value: unknown): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(value)))
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function rowsFromStatement(statement: any, limit = 18): any[][] {
  const periods = Array.isArray(statement?.periods) ? statement.periods : []
  const rows = Array.isArray(statement?.rows) ? statement.rows : []
  const header = ['Account', ...periods.slice(-5).map((period: any) => period?.label || period?.period || period)]
  const body = rows.filter((row: any) => row?.visible !== false).slice(0, limit).map((row: any) => {
    const values = Array.isArray(row?.values) ? row.values.slice(-5) : []
    return [row?.label || row?.name || row?.key || 'Line item', ...values.map(money)]
  })
  return body.length ? [header, ...body] : []
}

function tableAmount(value: unknown): string {
  const number = Number(value)
  if (!Number.isFinite(number)) return '—'
  const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.abs(number))
  return number < 0 ? `(${formatted})` : formatted
}

function rollupMatrix(statementType: string, statement: any, live: any): any[][] {
  const rollup = statementType === 'balance_sheet'
    ? balanceSheetRollupRows(statement, 12)
    : statementType === 'cash_flow'
      ? cashFlowRollupRows(statement, 12)
      : profitAndLossRollupRows(statement, 12, live?.config?.derivedMetrics)
  if (!rollup.periods.length || !rollup.rows.length) return []
  return [
    ['($)', ...rollup.periods.map(monthShort)],
    ...rollup.rows.map((row) => [
      row.label,
      ...row.values.map((value) => row.kind === 'percent' ? percentage(value) : tableAmount(value)),
    ]),
  ]
}

function cardsFromFlashMatrix(matrix: any): Array<{ label: string; value: string; detail?: string }> {
  if (!Array.isArray(matrix) || !Array.isArray(matrix[0])) return []
  const header = matrix[0].map((cell: unknown) => String(cell ?? '').trim().toLowerCase())
  const actualIndex = Math.max(1, header.indexOf('actual'))
  const forecastIndex = header.indexOf('forecast')
  const priorMonthIndex = header.indexOf('prior month')
  const preferred = ['revenue', 'gross profit', 'net income', 'cash & bank', 'total assets', 'net cash — operating activities']
  const rows = new Map<string, any[]>()
  for (const candidate of matrix.slice(1)) {
    if (!Array.isArray(candidate)) continue
    const label = String(candidate[0] ?? '').trim()
    if (label) rows.set(label.toLowerCase(), candidate)
  }
  return preferred.flatMap((wanted) => {
    const row = rows.get(wanted)
    if (!row) return []
    const value = String(row[actualIndex] ?? '').trim()
    if (!value || value === '–' || value === '—') return []
    const forecast = forecastIndex >= 0 ? String(row[forecastIndex] ?? '').trim() : ''
    const comparisonIndex = forecast && forecast !== '–' && forecast !== '—'
      ? forecastIndex
      : priorMonthIndex
    const comparison = comparisonIndex >= 0 ? String(row[comparisonIndex] ?? '').trim() : ''
    const detail = comparison && comparison !== '–' && comparison !== '—'
      ? `${header[comparisonIndex] === 'forecast' ? 'Forecast' : 'Prior month'} ${comparison}`
      : undefined
    return [{ label: String(row[0]), value, ...(detail ? { detail } : {}) }]
  })
}

function draftSections(spec: any): any[] {
  return Array.isArray(spec?.blocks)
    ? spec.blocks.map((block: any) => ({ block, section: block?.content?.snapshot_section })).filter((entry: any) => entry.section)
    : []
}

function draftSection(spec: any, ids: string[]): { block: any; section: any } | null {
  const wanted = new Set(ids)
  return draftSections(spec).find(({ block, section }) => wanted.has(String(section?.sectionId || block?.source?.section_id || block?.id || ''))) || null
}

function fallbackLibrarySourceIds(kind: string, libraryItem?: HybridReportLibraryBlock): string[] {
  const statementType = libraryItem?.statement_type || sourceConfig(libraryItem).statement_type || 'profit_and_loss'
  if (kind === 'statement_detail' || kind === 'statement_rollup') {
    if (statementType === 'balance_sheet') return ['bs_trend', 'bs_forecast']
    if (statementType === 'cash_flow') return ['cf_trend']
    return kind === 'statement_detail' ? ['pnl_month', 'pnl_trend'] : ['pnl_trend', 'pnl_month']
  }
  if (kind === 'trend_chart') {
    if (statementType === 'balance_sheet') return ['bs_trend']
    if (statementType === 'cash_flow') return ['cf_trend']
    return ['pnl_trend', 'pnl_month']
  }
  if (kind === 'kpi_grid') return ['executive_summary', 'kpi_flash']
  if (kind === 'ar_summary') return ['ar_aging_customer']
  if (kind === 'text') return ['executive_summary', 'variance_commentary']
  return []
}

function fallbackLibrarySource(kind: string, libraryItem: HybridReportLibraryBlock | undefined, spec: any) {
  const ids = fallbackLibrarySourceIds(kind, libraryItem)
  return ids.length ? draftSection(spec, ids) : null
}

function numericCell(value: unknown): number {
  const source = String(value ?? '').trim()
  if (!source || source === '–' || source === '—') return 0
  const negative = /^\(.*\)$/.test(source) || source.startsWith('-')
  const parsed = Number(source.replace(/[(),$%\s]/g, '')) || 0
  return negative ? -Math.abs(parsed) : parsed
}

function draftChart(spec: any, libraryItem: HybridReportLibraryBlock | undefined) {
  const config = sourceConfig(libraryItem as HybridReportLibraryBlock)
  const statementType = String(config.statement_type || 'profit_and_loss')
  const sourceIds = statementType === 'cash_flow' ? ['cf_trend']
    : statementType === 'balance_sheet' ? ['bs_trend']
      : ['pnl_trend']
  const source = draftSection(spec, sourceIds)?.section?.matrix
  if (!Array.isArray(source) || !Array.isArray(source[0])) return null
  if (config.metric === 'quarterly_revenue_net_income') {
    const rows = new Map(source.slice(1).filter(Array.isArray).map((row: any[]) => [String(row[0] || '').toLowerCase(), row]))
    const find = (pattern: RegExp) => Array.from(rows.entries()).find(([label]) => pattern.test(label))?.[1]
    const revenue = find(/^(total )?revenue$/i)
    const netIncome = find(/^net income$/i)
    const ebitda = find(/^ebitda$/i)
    if (!revenue || !netIncome || !ebitda) return null
    const quarters = new Map<string, { revenue: number; netIncome: number; ebitda: number }>()
    source[0].slice(1).forEach((period: unknown, index: number) => {
      const key = monthKey(period)
      if (!key) return
      const [year, month] = key.split('-').map(Number)
      const label = `Q${Math.floor((month - 1) / 3) + 1} ${String(year).slice(-2)}`
      const current = quarters.get(label) || { revenue: 0, netIncome: 0, ebitda: 0 }
      current.revenue += numericCell(revenue[index + 1])
      current.netIncome += numericCell(netIncome[index + 1])
      current.ebitda += numericCell(ebitda[index + 1])
      quarters.set(label, current)
    })
    const entries = Array.from(quarters.entries()).slice(-8)
    return entries.length ? {
      kind: 'combo',
      labels: entries.map(([label]) => label),
      series: [
        { label: 'Revenue', type: 'bar', values: entries.map(([, value]) => value.revenue) },
        { label: 'Net income', type: 'line', values: entries.map(([, value]) => value.netIncome) },
        { label: 'EBITDA', type: 'line', tone: 'secondary', values: entries.map(([, value]) => value.ebitda) },
      ],
      value_format: 'currency',
    } : null
  }
  const rowLabel = String(config.row_label || 'net income').toLowerCase()
  const row = source.find((candidate: any) => Array.isArray(candidate) && String(candidate[0] || '').toLowerCase().includes(rowLabel))
  if (!Array.isArray(row)) return null
  return {
    kind: 'bars',
    labels: source[0].slice(1),
    series: [{ label: String(row[0] || libraryItem?.title || 'Trend'), type: 'bar', values: row.slice(1).map(numericCell) }],
    value_format: 'currency',
  }
}

export function fallbackLibraryBlockContent(kind: string, libraryItem: HybridReportLibraryBlock | undefined, spec: any) {
  if (kind === 'statement_detail' || kind === 'statement_rollup') {
    const matrix = fallbackLibrarySource(kind, libraryItem, spec)?.section?.matrix
    return { matrix: Array.isArray(matrix) ? matrix : [] }
  }
  if (kind === 'kpi_grid') {
    const section = fallbackLibrarySource(kind, libraryItem, spec)?.section
    const cards = Array.isArray(section?.cards) ? section.cards : []
    return { cards: cards.length ? cards : cardsFromFlashMatrix(section?.matrix) }
  }
  if (kind === 'ar_summary') {
    const matrix = fallbackLibrarySource(kind, libraryItem, spec)?.section?.matrix
    return { matrix: Array.isArray(matrix) ? matrix : [] }
  }
  if (kind === 'action_list') {
    // A generic manual table is not a governed online action list. Treat the
    // block as unavailable until the online report supplies actual alerts,
    // priorities, or caveats instead of relabelling unrelated workbook rows.
    return { text: '' }
  }
  if (kind === 'ratio_card') return { unavailable: 'This governed ratio is not available from the captured Excel report alone.' }
  // A bridge needs governed comparison semantics. Monthly levels are not
  // deltas and must never be accumulated into a convincing-looking fallback.
  if (kind === 'waterfall') return { chart: null }
  if (kind === 'trend_chart') return { chart: draftChart(spec, libraryItem) }
  const section = fallbackLibrarySource(kind, libraryItem, spec)?.section
  const commentary = section?.commentary
  const text = typeof commentary === 'string'
    ? commentary
    : [commentary?.body, commentary?.summary, ...(Array.isArray(commentary?.paragraphs) ? commentary.paragraphs : [])].filter(Boolean).join('\n\n')
  return { text: text || 'Write the report narrative here.' }
}

function fallbackLibrarySourceHash(kind: string, libraryItem: HybridReportLibraryBlock | undefined, spec: any): string {
  if (kind === 'ratio_card') return ''
  return String(fallbackLibrarySource(kind, libraryItem, spec)?.block?.freshness?.sourceHash || spec?.source_context?.source_signature || '')
}

function libraryBlockUsesExcelCapture(item: HybridReportLibraryBlock, live: any, spec: any): boolean {
  if (!fallbackLibrarySource(item.kind, item, spec)) return false
  if (!live) return true
  const config = sourceConfig(item)
  const statementType = item.statement_type || config.statement_type || 'profit_and_loss'
  if (item.kind === 'statement_detail' || item.kind === 'statement_rollup' || item.kind === 'trend_chart') {
    return !live?.statements?.[statementType]
  }
  if (item.kind === 'ar_summary') return !live?.ar_analysis
  return false
}

function libraryChoiceSourceLabel(item: HybridReportLibraryBlock, live: any, spec: any): string {
  if (item.kind === 'saved_view') return 'Source: governed saved view'
  if (item.kind === 'ratio_card') return 'Source: governed ratio analysis'
  if (libraryBlockUsesExcelCapture(item, live, spec)) {
    const fallback = fallbackLibrarySource(item.kind, item, spec)
    const title = fallback?.section?.title || fallback?.block?.title || fallback?.section?.sectionId || fallback?.block?.id || 'captured report section'
    return `Source: Excel capture · ${title}`
  }
  if (!live) return 'Source: studio-authored narrative'
  return 'Source: FuseGrid online report'
}

function resolvedLibrarySource(item: HybridReportLibraryBlock, resolved: any) {
  return {
    library_kind: item.source_kind,
    governed_id: item.governed_id,
    display_name: item.title,
    ...sourceConfig(item),
    resolution: resolved.sourceResolution,
    ...(resolved.fallbackSectionId ? { fallback_section_id: resolved.fallbackSectionId } : {}),
    ...(resolved.fallbackSectionTitle ? { fallback_section_title: resolved.fallbackSectionTitle } : {}),
  }
}

function blockOriginLabel(block: any): string {
  if (block?.origin !== 'web_library') return block?.origin === 'excel_binding' ? 'Live Excel binding' : 'Excel report section'
  if (block?.source?.resolution === 'excel_capture') return 'Governed block derived from Excel'
  if (block?.source?.resolution === 'saved_view') return 'Governed saved view'
  if (block?.source?.resolution === 'ratio_analysis') return 'Governed ratio analysis'
  if (block?.source?.resolution === 'studio_draft') return 'Studio-authored narrative'
  return 'FuseGrid online report'
}

function blockSourceLabel(block: any): string {
  const source = block?.source || {}
  if (source.resolution === 'excel_capture') {
    return `Excel capture · ${source.fallback_section_title || source.fallback_section_id || source.display_name || 'report section'}`
  }
  if (source.sheet_name) {
    return `${source.sheet_name}${source.address ? ` · ${String(source.address).split('!').at(-1)}` : source.chart_name ? ` · ${source.chart_name}` : ''}`
  }
  return source.display_name || source.library_kind || source.section_id || 'Governed report'
}

export function governedLibraryBlockContent(kind: string, live: any, ratios: RatioAnalysisResponse | null, libraryItem?: HybridReportLibraryBlock, spec?: any) {
  const config = libraryItem ? sourceConfig(libraryItem) : {}
  const reportMonth = effectiveHybridReportMonth(spec)
  // Ratio analysis has its own governed endpoint. It remains usable if the
  // broader online presentation request is unavailable.
  if (kind === 'ratio_card') {
    const ratioId = String(config.ratio_id || '')
    const card = ratioById(ratios, ratioId)
    if (!card) throw new Error(`${libraryItem?.title || 'This ratio'} is not available for the selected report period.`)
    const model = buildRatioCardBlockModel({ [ratioId]: card }, {
      source: { ratio_id: ratioId },
      content: { card: pinnedRatioCardSnapshot(card) },
    })
    if (!model) throw new Error(`${card.label} could not be prepared for the report.`)
    return { ratio_card: model }
  }
  if (!live) return fallbackLibraryBlockContent(kind, libraryItem, spec)
  const statements = live?.statements || {}
  if (kind === 'kpi_grid') {
    const section = String(config.section || 'home')
    return {
      cards: kpiCardsForSection(reportMonthMetrics(live, reportMonth), section).map((card) => ({
        ...card,
        detail: card.detail || `${monthName(reportMonth)} actual`,
      })),
    }
  }
  if (kind === 'statement_detail' || kind === 'statement_rollup') {
    const statementType = libraryItem?.statement_type || config.statement_type || 'profit_and_loss'
    if (!statements[statementType]) return fallbackLibraryBlockContent(kind, libraryItem, spec)
    const bounded = statementThroughMonth(statements[statementType], reportMonth)
    return {
      matrix: kind === 'statement_rollup'
        ? rollupMatrix(statementType, bounded, live)
        : rowsFromStatement(bounded, 24),
    }
  }
  if (kind === 'ar_summary') {
    if (!live?.ar_analysis) return fallbackLibraryBlockContent(kind, libraryItem, spec)
    const concerns = live?.ar_analysis?.topCustomerConcerns || live?.ar_analysis?.customerTrends || []
    return { matrix: [['Customer', 'A/R', 'Status'], ...concerns.slice(0, 12).map((row: any) => [row.customer || row.customerName || 'Customer', money(row.endingAr), row.status || 'Review'])] }
  }
  if (kind === 'action_list') {
    const commentary = live?.config?.commentary || {}
    const groups = [
      ['Alerts', commentary.alerts],
      ['Priorities', commentary.actions],
      ['Data caveats', commentary.dataCaveats],
    ].flatMap(([heading, entries]: any[]) => Array.isArray(entries) && entries.length
      ? [`**${heading}**\n\n${entries.map((item: string) => `- ${item}`).join('\n')}`]
      : [])
    return { text: groups.join('\n\n') }
  }
  if (kind === 'waterfall') {
    if (!statements.profit_and_loss) return fallbackLibraryBlockContent(kind, libraryItem, spec)
    const statement = statementThroughMonth(statements.profit_and_loss, reportMonth)
    const comparisonsMatchMonth = !reportMonth || monthKey(live?.source_state?.reporting_period) === reportMonth
    const bridge = computeWaterfallBridge(
      statement,
      live?.metrics || {},
      24,
      { mode: config.mode || 'mom' },
      comparisonsMatchMonth ? live?.metric_comparisons : null,
    )
    if (!bridge) throw new Error(`${libraryItem?.title || 'This net income bridge'} is not available for the selected report period.`)
    return {
      chart: {
        kind: 'waterfall',
        steps: bridge.steps,
        value_format: 'currency',
        note: bridge.hasBridge
          ? `${bridge.mode === 'yoy' ? 'Year-over-year' : 'Month-over-month'} net income movement from ${monthName(bridge.priorPeriod)} to ${monthName(bridge.latestPeriod)}.`
          : 'Latest-period net income composition.',
      },
    }
  }
  if (kind === 'trend_chart') {
    const statementType = String(config.statement_type || 'profit_and_loss')
    if (!statements[statementType]) return fallbackLibraryBlockContent(kind, libraryItem, spec)
    const chart = config.metric === 'quarterly_revenue_net_income'
      ? quarterlyRevenueNetIncomeChart(statements.profit_and_loss, reportMonth)
      : statementTrendChart(statements[statementType], String(config.row_label || 'net income'), reportMonth)
    if (!chart) throw new Error(`${libraryItem?.title || 'This trend chart'} is not available for the selected report period.`)
    return { chart }
  }
  return { text: live?.config?.commentary?.executiveSummary || 'Write the report narrative here.' }
}

const waitFor = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds))

function downloadBlob(blob: Blob, title: string) {
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `${safeId(title || 'financial-report') || 'financial-report'}.pdf`
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(link.href), 10_000)
}

export function shouldRefreshHybridDraft(current: HybridReportDraft | null, next: HybridReportDraft): boolean {
  return !current
    || next.revision > current.revision
    || next.status !== current.status
    || next.excel_connected !== current.excel_connected
}

export function hybridReportApplyOutcome(previous: string, next: string): 'completed' | 'conflict' | null {
  if (previous !== 'apply_queued') return null
  if (next === 'applied') return 'completed'
  if (next === 'conflict') return 'conflict'
  return null
}

export function hybridPreviewBlockId(target: EventTarget | null): string {
  // srcDoc owns a separate DOM realm, so `target instanceof Element` is false
  // even for a real element in the preview iframe.
  const element = target && typeof (target as Element).closest === 'function'
    ? target as Element
    : null
  const container = element?.closest<HTMLElement>('article[data-report-block-id], section[data-report-block-id]')
  const id = String(container?.dataset.reportBlockId || '').trim()
  // Grid page wrappers carry a space-separated list of their children. Only
  // an exact stable block marker may drive selection in the editor.
  return /^[a-z0-9][a-z0-9_.-]{0,63}$/i.test(id) ? id : ''
}

export function hybridPreviewPlacements(document: Document): Record<string, number[]> {
  const placements = new Map<string, Set<number>>()
  Array.from(document.querySelectorAll<HTMLElement>('.report-page')).forEach((page, index) => {
    const markers = [
      ...(page.dataset.reportBlockId ? [page] : []),
      ...Array.from(page.querySelectorAll<HTMLElement>('[data-report-block-id]')),
    ]
    for (const marker of markers) {
      const id = String(marker.dataset.reportBlockId || '').trim()
      if (!/^[a-z0-9][a-z0-9_.-]{0,63}$/i.test(id)) continue
      const pages = placements.get(id) || new Set<number>()
      pages.add(index + 1)
      placements.set(id, pages)
    }
  })
  return Object.fromEntries(Array.from(placements, ([id, pages]) => [id, Array.from(pages).sort((left, right) => left - right)]))
}

export function HybridExcelReportStudio({ appApi, workbookId, draftId, modulesOverride }: Props) {
  const analytics = useAnalytics()
  const [modules, setModules] = useState<HybridModules | null>(null)
  const [draft, setDraft] = useState<HybridReportDraft | null>(null)
  const [library, setLibrary] = useState<HybridReportLibraryBlock[]>([])
  const [live, setLive] = useState<any>(null)
  const [ratios, setRatios] = useState<RatioAnalysisResponse | null>(null)
  const [selectedId, setSelectedId] = useState('')
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewHash, setPreviewHash] = useState('')
  const [previewHeight, setPreviewHeight] = useState(1056)
  const [previewPages, setPreviewPages] = useState(0)
  const [previewPlacements, setPreviewPlacements] = useState<Record<string, number[]>>({})
  const [draggedId, setDraggedId] = useState('')
  const [busy, setBusy] = useState('')
  const [message, setMessage] = useState('Loading report draft…')
  const [aiPrompt, setAiPrompt] = useState('')
  const [commentaryPrompt, setCommentaryPrompt] = useState('')
  const commentaryEditorRef = useRef<HTMLTextAreaElement | null>(null)
  const readyTelemetryDrafts = useRef(new Set<string>())
  const previewTelemetryRevisions = useRef(new Set<string>())
  const priorDraftStatus = useRef('')

  const load = useCallback(async () => {
    const sharedModules = modulesOverride
      ? Promise.resolve([modulesOverride, modulesOverride] as const)
      : Promise.all([
          import(/* @vite-ignore */ hybridSpecModuleUrl),
          import(/* @vite-ignore */ reportHtmlModuleUrl),
        ])
    const [specModule, htmlModule, nextDraft, nextLibrary, nextLive] = await Promise.all([
      sharedModules.then((loaded) => loaded[0]),
      sharedModules.then((loaded) => loaded[1]),
      appApi.getHybridReportDraft(workbookId, draftId),
      appApi.listHybridReportLibrary(workbookId),
      appApi.getCfoPresentation(workbookId).catch(() => null),
    ])
    const reportMonth = effectiveHybridReportMonth(nextDraft.spec)
    const nextRatios = typeof appApi.getCfoRatios === 'function'
      ? await appApi.getCfoRatios(
          workbookId,
          reportMonth ? { asOf: `${reportMonth}-01` } : undefined,
        ).catch(() => null)
      : null
    const eligibleLibrary = nextLibrary.filter((item) => libraryItemAvailable(item, nextLive, nextRatios, nextDraft.spec))
    setModules({ ...specModule, ...htmlModule } as HybridModules)
    setDraft(nextDraft)
    setLibrary(eligibleLibrary)
    setLive(nextLive)
    setRatios(nextRatios)
    setSelectedId((current) => nextDraft.spec?.blocks?.some((block: any) => block.id === current)
      ? current
      : nextDraft.spec?.blocks?.[0]?.id || '')
    setMessage(`Draft ready · ${eligibleLibrary.length} governed choices available`)
  }, [appApi, draftId, modulesOverride, workbookId])

  useEffect(() => { load().catch((error) => setMessage(error?.message || 'Could not open this report draft.')) }, [load])

  useEffect(() => {
    if (!draft || !modules || readyTelemetryDrafts.current.has(draft.draft_id)) return
    readyTelemetryDrafts.current.add(draft.draft_id)
    analytics.track({
      event: 'hybrid_report_studio_ready',
      surface: 'hybrid_report_studio',
      workbook_id: workbookId,
      props: {
        revision: draft.revision,
        block_count: draft.spec?.blocks?.length || 0,
        excel_connected: draft.excel_connected === true,
      },
    })
  }, [analytics, draft, modules, workbookId])

  useEffect(() => {
    if (!draft) return
    const outcome = hybridReportApplyOutcome(priorDraftStatus.current, draft.status)
    priorDraftStatus.current = draft.status
    if (!outcome) return
    analytics.track({
      event: outcome === 'completed' ? 'hybrid_report_apply_completed' : 'hybrid_report_apply_conflict',
      surface: 'hybrid_report_studio',
      workbook_id: workbookId,
      props: { revision: draft.revision },
    })
  }, [analytics, draft, workbookId])

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (busy) return
      appApi.getHybridReportDraft(workbookId, draftId).then((next) => {
        setDraft((current) => shouldRefreshHybridDraft(current, next) ? next : current)
      }).catch(() => {})
    }, 15_000)
    return () => window.clearInterval(timer)
  }, [appApi, busy, draftId, workbookId])

  useEffect(() => {
    if (!modules || !draft) return
    let cancelled = false
    try {
      const snapshot = modules.snapshotFromHybridReportSpec(draft.spec)
      const rendered = modules.buildReportHtml(snapshot)
      setPreviewHtml(rendered.html)
      setPreviewHash('')
      crypto.subtle.digest('SHA-256', new TextEncoder().encode(rendered.html)).then((digest) => {
        if (!cancelled) setPreviewHash(Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join(''))
      })
    } catch (error: any) {
      setMessage(error?.message || 'Preview could not be rendered.')
    }
    return () => { cancelled = true }
  }, [draft, modules])

  const blocks = draft?.spec?.blocks || []
  const coverBlock = blocks.find((block: any) => block.kind === 'cover_toc') || null
  const reportFooterText = Object.prototype.hasOwnProperty.call(coverBlock?.configuration || {}, 'footerText')
    ? coverBlock.configuration.footerText
    : draft?.spec?.branding?.footerText ?? 'Confidential'
  const reportFooterDisclaimer = coverBlock?.configuration?.footerDisclaimer || ''
  const selected = blocks.find((block: any) => block.id === selectedId) || null
  const selectedChart = selected?.content?.chart || selected?.content?.snapshot_section?.hybridChart || selected?.content?.snapshot_section?.chart
  const selectedChartSupportsWaterfall = hybridChartSupportsWaterfall(selectedChart)
  const selectedPreviewPages = selected ? previewPlacements[selected.id] || [] : []
  const selectedMatrix = selected?.content?.matrix || selected?.content?.snapshot_section?.matrix || []
  const selectedDataRowCount = Math.max(0, selectedMatrix.length - (selected?.configuration?.showTableHeader === false ? 0 : 1))
  const selectedRowBudget = selectedMatrix.length
    ? modules?.reportRowsPerPage({ type: selected.kind, hybridLayout: selected.layout }) || 0
    : 0
  const selectedCommentary = selected && modules
    ? modules.hybridReportBlockCommentary(draft?.spec, selected)
    : null
  const selectedCanEditCommentary = hybridBlockHasEditableCommentary(selected)
  const reportMonth = effectiveHybridReportMonth(draft?.spec)
  const workbookCloseLabel = draft?.spec?.document?.closeThroughLabel || monthName(reportMonth)
  const exactOnlineDataThrough = live?.source_state?.data_through || ''
  const onlineReportingPeriod = live?.source_state?.reporting_period || ''
  const onlineDataThrough = exactOnlineDataThrough || onlineReportingPeriod
  // `data_through` is an exact source cutoff and may be a partial month. Only
  // the fallback reporting-period key represents a whole accounting month.
  // Turning an Aug 24 cutoff into Aug 31 would overstate source freshness.
  const onlineDataThroughLabel = exactOnlineDataThrough
    ? dayName(exactOnlineDataThrough)
    : periodEndDayName(onlineReportingPeriod)
  const newerOnlineData = Boolean(
    reportMonth
    && monthKey(onlineDataThrough)
    && monthKey(onlineDataThrough) > reportMonth,
  )
  const libraryGroups = library.reduce((groups, item) => {
    const key = item.category || (item.kind === 'saved_view' ? 'Saved governed views' : 'Governed report blocks')
    groups.set(key, [...(groups.get(key) || []), item])
    return groups
  }, new Map<string, HybridReportLibraryBlock[]>())

  useEffect(() => {
    if (!draft || !previewHash || !previewPages) return
    const key = `${draft.revision}:${previewHash}`
    if (previewTelemetryRevisions.current.has(key)) return
    previewTelemetryRevisions.current.add(key)
    const telemetryBlocks = Array.isArray(draft.spec?.blocks) ? draft.spec.blocks : []
    const excelBlocks = telemetryBlocks.filter((block: any) => String(block.origin || '').startsWith('excel_'))
    analytics.track({
      event: 'hybrid_report_preview_ready',
      surface: 'hybrid_report_studio',
      workbook_id: workbookId,
      props: {
        revision: draft.revision,
        page_count: previewPages,
        block_count: telemetryBlocks.length,
        excel_block_count: excelBlocks.length,
        stale_excel_block_count: excelBlocks.filter((block: any) => block.freshness?.stale).length,
      },
    })
  }, [analytics, draft, previewHash, previewPages, workbookId])

  const commit = useCallback(async (nextSpec: any, summary: string, actor: 'user' | 'ai' = 'user') => {
    if (!draft) return false
    setBusy('save')
    try {
      const next = await appApi.updateHybridReportDraft(draftId, {
        workbook_id: workbookId,
        expected_revision: draft.revision,
        spec: nextSpec,
        actor_kind: actor,
        change_summary: summary,
      })
      setDraft(next)
      setMessage(`${summary} · revision ${next.revision}`)
      return true
    } catch (error: any) {
      if (/revision|conflict/i.test(String(error?.message || error))) await load()
      setMessage(error?.message || 'Could not save this edit.')
      return false
    } finally {
      setBusy('')
    }
  }, [appApi, draft, draftId, load, workbookId])

  const patch = async (action: any, summary: string, actor: 'user' | 'ai' = 'user') => {
    if (!draft || !modules) return false
    return await commit(modules.hybridReportPatch(draft.spec, action), summary, actor)
  }

  const resolveLibraryBlock = useCallback(async (item: HybridReportLibraryBlock) => {
    if (item.kind !== 'saved_view') {
      const excelCapture = libraryBlockUsesExcelCapture(item, live, draft?.spec)
      const fallback = excelCapture ? fallbackLibrarySource(item.kind, item, draft?.spec) : null
      const sourceResolution = item.kind === 'ratio_card'
        ? 'ratio_analysis'
        : excelCapture
          ? 'excel_capture'
          : live
            ? 'online_report'
            : 'studio_draft'
      return {
        content: governedLibraryBlockContent(item.kind, live, ratios, item, draft?.spec),
        sourceHash: excelCapture
          ? fallbackLibrarySourceHash(item.kind, item, draft?.spec)
          : item.source_hash || live?.source_state?.signature || fallbackLibrarySourceHash(item.kind, item, draft?.spec),
        capturedAt: excelCapture
          ? fallback?.block?.freshness?.capturedAt || new Date().toISOString()
          : live?.source_state?.last_successful_refresh || new Date().toISOString(),
        sourceResolution,
        fallbackSectionId: excelCapture ? String(fallback?.section?.sectionId || fallback?.block?.source?.section_id || fallback?.block?.id || '') : '',
        fallbackSectionTitle: excelCapture ? String(fallback?.section?.title || fallback?.block?.title || '') : '',
        stale: excelCapture && fallback?.block?.freshness?.stale === true,
      }
    }
    if (!item.semantic_spec || !item.governed_id) throw new Error('This saved view has no governed semantic definition.')
    setMessage(`Preparing ${item.title} from governed data…`)
    let materialization = await appApi.materializeHybridReportSavedView(workbookId, item.governed_id)
    const deadline = Date.now() + 90_000
    while (!['ready', 'expired'].includes(materialization.status)) {
      if (['failed', 'cancelled'].includes(materialization.status) || materialization.error_code) {
        throw new Error(materialization.error_code || 'The governed saved view could not be materialized.')
      }
      if (Date.now() >= deadline) throw new Error('The governed saved view is still being prepared. Try adding it again shortly.')
      await waitFor(1_000)
      materialization = await appApi.getHybridReportLibraryMaterialization(workbookId, materialization.data_product_id)
    }
    // The studio supports at most 100 displayed data rows per block. Keep the
    // persisted draft bounded to that same governed display contract instead
    // of embedding an arbitrarily large materialization page.
    const page = await appApi.getHybridReportLibraryMaterializationRows(workbookId, materialization.data_product_id, 100)
    return {
      content: { matrix: [page.columns || [], ...(page.rows || [])] },
      sourceHash: page.object_sha256 || page.exact_value_sha256 || item.source_hash || '',
      capturedAt: page.source_as_of || materialization.source_as_of || new Date().toISOString(),
      sourceResolution: 'saved_view',
      fallbackSectionId: '',
      fallbackSectionTitle: '',
      stale: false,
    }
  }, [appApi, draft, live, ratios, workbookId])

  const addLibraryBlock = async (item: HybridReportLibraryBlock) => {
    if (!draft || !modules) return
    setBusy('library')
    try {
      const resolved = await resolveLibraryBlock(item)
      const id = `${safeId(item.kind)}-${Date.now().toString(36)}`
      const resolvedSourceHash = /^[0-9a-f]{64}$/i.test(String(resolved.sourceHash || ''))
        ? String(resolved.sourceHash).toLowerCase()
        : await sha256Json(resolved.content)
      const block = {
        id, origin: 'web_library', kind: item.kind, title: item.title, section: 'web', enabled: true,
        layout: { span: item.default_span === 6 ? 6 : 12, density: 'standard', keepTogether: true, pageBreakBefore: false },
        appearance: { tone: 'default', titleAlignment: 'left', showSourceLabel: true },
        configuration: { maxRows: null, maxCards: 5, showTableHeader: true, chartStyle: 'auto' },
        source: resolvedLibrarySource(item, resolved),
        freshness: { sourceHash: resolvedSourceHash, capturedAt: resolved.capturedAt, stale: resolved.stale === true },
        content: resolved.content,
      }
      if (await commit(modules.hybridReportPatch(draft.spec, { op: 'add', block }), `Added ${item.title}`)) {
        setSelectedId(id)
        analytics.track({
          event: 'hybrid_report_block_added',
          surface: 'hybrid_report_studio',
          workbook_id: workbookId,
          props: { block_kind: item.kind, source_kind: item.source_kind },
        })
      }
    } catch (error: any) {
      setMessage(error?.message || `Could not add ${item.title}.`)
    } finally {
      setBusy('')
    }
  }

  const applyAiEdit = async (requestedPrompt = aiPrompt, targetBlockId = selected?.id || null, commentaryRewrite = false) => {
    if (!draft || !modules || !requestedPrompt.trim()) return
    setBusy('ai')
    try {
      const response = await appApi.proposeHybridReportAiEdit(
        workbookId,
        draftId,
        draft.revision,
        requestedPrompt.trim(),
        targetBlockId,
      )
      const operations = (response.operations || []).map((operation: any) => {
        if (operation?.op !== 'update') return operation
        const block = draft.spec?.blocks?.find((candidate: any) => candidate.id === operation.block_id)
        const changes = { ...(operation.changes || {}) }
        if (changes.layout) changes.layout = { ...(block?.layout || {}), ...changes.layout }
        if (changes.appearance) changes.appearance = { ...(block?.appearance || {}), ...changes.appearance }
        if (changes.configuration) {
          const requestedCommentary = changes.configuration.commentary
          changes.configuration = { ...(block?.configuration || {}), ...changes.configuration }
          if (requestedCommentary && block) {
            changes.configuration.commentary = modules.hybridReportCommentaryOverride(
              draft.spec,
              block,
              requestedCommentary.body,
              'ai',
            )
          }
        }
        return { ...operation, changes }
      })
      const next = await applyHybridReportAiOperations({
        spec: draft.spec,
        operations,
        library,
        normalize: modules.normalizeHybridReportSpec,
        patch: modules.hybridReportPatch,
        addCatalogBlock: async (current, catalog, requested) => {
          const resolved = await resolveLibraryBlock(catalog)
          const resolvedSourceHash = /^[0-9a-f]{64}$/i.test(String(resolved.sourceHash || ''))
            ? String(resolved.sourceHash).toLowerCase()
            : await sha256Json(resolved.content)
          const id = uniqueBlockId(current.blocks, safeId(requested.id || `${requested.kind}-${Date.now().toString(36)}`))
          return modules.hybridReportPatch(current, { op: 'add', block: {
            id, origin: 'web_library', kind: catalog.kind, title: requested.title || catalog.title,
            section: requested.section || 'web', enabled: requested.enabled !== false,
            layout: { span: requested.layout?.span === 6 ? 6 : 12, density: requested.layout?.density || 'standard', keepTogether: requested.layout?.keepTogether !== false, pageBreakBefore: requested.layout?.pageBreakBefore === true },
            appearance: { tone: requested.appearance?.tone || 'default', titleAlignment: requested.appearance?.titleAlignment || 'left', showSourceLabel: requested.appearance?.showSourceLabel !== false },
            configuration: { maxRows: requested.configuration?.maxRows ?? null, maxCards: requested.configuration?.maxCards ?? 5, showTableHeader: requested.configuration?.showTableHeader !== false, chartStyle: requested.configuration?.chartStyle || 'auto' },
            source: resolvedLibrarySource(catalog, resolved),
            freshness: { sourceHash: resolvedSourceHash, capturedAt: resolved.capturedAt, stale: resolved.stale === true },
            content: { ...resolved.content, ...(requested.content?.text ? { text: requested.content.text } : {}) },
          } })
        },
      })
      if (await commit(next, response.change_summary || 'AI refined report blocks', 'ai')) {
        if (commentaryRewrite) setCommentaryPrompt('')
        else setAiPrompt('')
        analytics.track({
          event: 'hybrid_report_ai_edit_applied',
          surface: 'hybrid_report_studio',
          workbook_id: workbookId,
          props: { operation_count: operations.length, ...(commentaryRewrite ? { commentary_rewrite: true } : {}) },
        })
      }
    } catch (error: any) {
      setMessage(error?.message || 'AI could not refine the report.')
    } finally {
      setBusy('')
    }
  }

  const saveSelectedCommentary = async (body: string, summary: string) => {
    if (!draft || !modules || !selected || !selectedCanEditCommentary) return false
    return await patch({
      op: 'update',
      block_id: selected.id,
      changes: {
        configuration: {
          ...selected.configuration,
          commentary: modules.hybridReportCommentaryOverride(draft.spec, selected, body, 'user'),
        },
      },
    }, summary)
  }

  const checkExcelConnection = async () => {
    if (!draft) return
    setBusy('connection')
    try {
      const next = await appApi.getHybridReportDraft(workbookId, draftId)
      setDraft(next)
      setMessage(next.excel_connected
        ? 'Excel sync is active.'
        : 'The report is ready from the last capture. Keep the workbook and FuseGrid Workspace open to resume automatic updates.')
    } catch (error: any) {
      setMessage(error?.message || 'Could not check the Excel connection.')
    } finally {
      setBusy('')
    }
  }

  const exportPdf = async () => {
    if (!previewHtml || !previewHash || !draft) return
    const startedAt = performance.now()
    setBusy('pdf')
    try {
      const blob = await appApi.renderHybridReportPdf(workbookId, draftId, {
        expected_revision: draft.revision,
        html_sha256: previewHash,
        html: previewHtml,
        title: draft.spec?.document?.companyName || 'Financial report',
      })
      downloadBlob(blob, draft.spec?.document?.companyName || 'Financial report')
      setMessage(`PDF downloaded from displayed HTML · ${previewHash.slice(0, 12)}`)
      analytics.track({
        event: 'hybrid_report_pdf_completed',
        surface: 'hybrid_report_studio',
        workbook_id: workbookId,
        props: { revision: draft.revision, page_count: previewPages, elapsed_ms: Math.round(performance.now() - startedAt) },
      })
    } catch (error: any) {
      analytics.track({
        event: 'hybrid_report_pdf_failed',
        surface: 'hybrid_report_studio',
        workbook_id: workbookId,
        props: { revision_conflict: /revision|conflict/i.test(String(error?.message || error)), elapsed_ms: Math.round(performance.now() - startedAt) },
      })
      setMessage(error?.message || 'PDF could not be generated.')
    } finally { setBusy('') }
  }

  const undoEdit = async () => {
    if (!draft) return
    setBusy('undo')
    try {
      setDraft(await appApi.undoHybridReportDraft(workbookId, draftId, draft.revision))
      setMessage('Undid the last edit')
      analytics.track({ event: 'hybrid_report_undo_completed', surface: 'hybrid_report_studio', workbook_id: workbookId, props: { from_revision: draft.revision } })
    } catch (error: any) {
      if (/revision|conflict/i.test(String(error?.message || error))) await load()
      setMessage(error?.message || 'Nothing to undo')
    } finally {
      setBusy('')
    }
  }

  const applyToWorkbook = async () => {
    if (!draft) return
    setBusy('apply')
    try {
      await appApi.applyHybridReportDraft(workbookId, draftId, draft.revision, draft.base_registry_hash)
      setDraft({ ...draft, status: 'apply_queued' })
      setMessage('Queued for the registered Excel workbook')
      analytics.track({ event: 'hybrid_report_apply_queued', surface: 'hybrid_report_studio', workbook_id: workbookId, props: { revision: draft.revision } })
    } catch (error: any) {
      if (/revision|conflict/i.test(String(error?.message || error))) await load()
      setMessage(error?.message || 'Could not queue workbook apply')
    } finally {
      setBusy('')
    }
  }

  const duplicateBlock = async (block: any) => {
    const nextId = uniqueBlockId(blocks, `${block.id}-copy`)
    if (await patch({ op: 'duplicate', block_id: block.id, new_id: nextId }, `Duplicated ${block.title}`)) {
      setSelectedId(nextId)
    }
  }

  const removeBlock = async (block: any) => {
    const index = blocks.findIndex((candidate: any) => candidate.id === block.id)
    const fallback = blocks[index + 1]?.id || blocks[index - 1]?.id || ''
    if (await patch({ op: 'remove', block_id: block.id }, `Removed ${block.title}`)) {
      setSelectedId(fallback)
    }
  }

  const moveBlock = async (block: any, toIndex: number) => {
    const bounded = Math.max(0, Math.min(blocks.length - 1, toIndex))
    if (bounded === blocks.findIndex((candidate: any) => candidate.id === block.id)) return false
    return await patch({ op: 'move', block_id: block.id, to_index: bounded }, `Moved ${block.title}`)
  }

  const bindPreviewFrame = (frame: HTMLIFrameElement) => {
    const document = frame.contentDocument
    if (!document) return
    const pages = document.querySelectorAll('.report-page').length
    setPreviewPages(pages)
    setPreviewPlacements(hybridPreviewPlacements(document))
    const height = Math.max(1056, document.documentElement.scrollHeight, document.body?.scrollHeight || 0)
    setPreviewHeight(height)
    // The iframe contains the exact export document. Wire selection at runtime
    // without injecting editor-only markup or styles into the HTML that is
    // hashed and sent to Chromium for the PDF.
    document.onclick = (event) => {
      const id = hybridPreviewBlockId(event.target)
      if (id && blocks.some((block: any) => block.id === id)) {
        setSelectedId(id)
        const target = event.target as Element | null
        if (target && typeof target.closest === 'function' && target.closest('.note')) {
          window.setTimeout(() => {
            window.document.getElementById('fg-hybrid-commentary-editor')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }, 0)
        }
      }
    }
  }

  if (!draft || !modules) return <main className="fg-hybrid-report-loading"><h1>Report composition studio</h1><p>{message}</p></main>

  return <main className="fg-hybrid-report-studio">
    <header className="fg-hybrid-report-toolbar">
      <div><p>Hybrid report · Excel sections + governed FuseGrid blocks</p><h1>{draft.spec?.document?.companyName || 'Financial report'}</h1></div>
      <span className={`fg-hybrid-status fg-hybrid-status-${draft.status}`}>{draft.status.replace('_', ' ')}</span>
      <span className={`fg-hybrid-status ${draft.excel_connected ? '' : 'fg-hybrid-status-paused'}`}>{draft.excel_connected ? 'Excel sync active' : 'Using last Excel capture'}</span>
      <button onClick={undoEdit} disabled={!!busy}><Redo2 size={16}/> Undo</button>
      <button onClick={applyToWorkbook} disabled={!!busy}><Upload size={16}/> Apply to workbook</button>
      <button className="primary" onClick={exportPdf} disabled={!!busy || !previewHash}><Download size={16}/> Download PDF</button>
    </header>
    <section className="fg-hybrid-period-contract" data-newer-data={newerOnlineData ? 'true' : 'false'}>
      <div><span>Reporting month</span><strong>{monthName(reportMonth)}</strong><small>This is the month the report analyzes.</small></div>
      <div><span>Workbook close state</span><strong>{draft.spec?.document?.closeEstimated ? 'Estimated through' : 'Closed through'} {workbookCloseLabel}</strong><small>Captured from the registered Excel workbook.</small></div>
      <div><span>Online governed data</span><strong>{onlineDataThrough ? `Available through ${onlineDataThroughLabel}` : 'No newer period reported'}</strong><small>Freshness only; it does not silently change the report month.</small></div>
      {!draft.excel_connected ? <p className="fg-hybrid-sync-notice" role="status"><span><strong>Your report is still ready.</strong> The preview and PDF use the last captured Excel values.{draft.excel_last_seen_at ? ` Last Excel check-in: ${timestampName(draft.excel_last_seen_at)}.` : ''} To resume automatic updates, keep the workbook and FuseGrid Workspace sidebar open; reload Workspace if it was closed.</span><button onClick={checkExcelConnection} disabled={!!busy}><RefreshCw size={13}/> Check Excel connection</button></p> : null}
      {newerOnlineData ? <p role="alert"><strong>This remains a {monthName(reportMonth)} report.</strong> Newer online data is available. To report {monthName(onlineDataThrough)}, return to Excel Workspace → Report settings, choose that month (or Follow latest governed close), save and rebuild the report, then reopen this studio.</p> : null}
    </section>
    <div className="fg-hybrid-report-workspace">
      <aside className="fg-hybrid-report-rail">
        <h2>Report blocks</h2>
        {blocks.map((block: any, index: number) => <div
          key={block.id}
          draggable={!busy && block.kind !== 'cover_toc'}
          className={`fg-hybrid-block-row ${selectedId === block.id ? 'selected' : ''} ${draggedId === block.id ? 'dragging' : ''} ${block.enabled === false ? 'hidden-block' : ''}`}
          onClick={() => setSelectedId(block.id)}
          onDragStart={(event) => { setDraggedId(block.id); event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', block.id) }}
          onDragEnd={() => setDraggedId('')}
          onDragOver={(event) => { if (block.kind !== 'cover_toc' && draggedId && draggedId !== block.id) { event.preventDefault(); event.dataTransfer.dropEffect = 'move' } }}
          onDrop={(event) => { event.preventDefault(); const id = event.dataTransfer.getData('text/plain') || draggedId; const dragged = blocks.find((candidate: any) => candidate.id === id); if (dragged) moveBlock(dragged, index); setDraggedId('') }}
        >
          <GripVertical className="fg-hybrid-drag-handle" size={15} aria-label={`Drag ${block.title}`}/>
          <span><strong>{block.title}</strong><small>{blockOriginLabel(block)}{block.layout?.pageBreakBefore ? ' · page break' : ''}{block.freshness?.stale ? ' · stale data' : ''}</small></span>
          <div className="fg-hybrid-block-actions">
            <button title="Move up" aria-label={`Move ${block.title} up`} disabled={block.kind === 'cover_toc' || index === 0 || (blocks[0]?.kind === 'cover_toc' && index === 1) || !!busy} onClick={(event) => { event.stopPropagation(); moveBlock(block, index - 1) }}><ArrowUp size={12}/></button>
            <button title="Move down" aria-label={`Move ${block.title} down`} disabled={block.kind === 'cover_toc' || index === blocks.length - 1 || !!busy} onClick={(event) => { event.stopPropagation(); moveBlock(block, index + 1) }}><ArrowDown size={12}/></button>
            <button title={block.kind === 'cover_toc' ? 'The report cover is required' : block.enabled === false ? 'Show block' : 'Hide block'} aria-label={`${block.enabled === false ? 'Show' : 'Hide'} ${block.title}`} disabled={block.kind === 'cover_toc' || !!busy} onClick={(event) => { event.stopPropagation(); patch({ op: 'update', block_id: block.id, changes: { enabled: block.enabled === false } }, `${block.enabled === false ? 'Showed' : 'Hid'} ${block.title}`) }}>{block.enabled === false ? <Eye size={12}/> : <EyeOff size={12}/>}</button>
            <button title={block.kind === 'cover_toc' ? 'The report cover always starts page 1' : 'Toggle page break'} aria-label={`Toggle page break before ${block.title}`} disabled={block.kind === 'cover_toc' || !!busy} onClick={(event) => { event.stopPropagation(); patch({ op: 'update', block_id: block.id, changes: { layout: { ...block.layout, pageBreakBefore: block.layout?.pageBreakBefore !== true } } }, `Changed page break for ${block.title}`) }}>↵</button>
            <button title={hybridBlockHasEditableCommentary(block) ? 'Edit commentary' : 'This block has no commentary'} aria-label={`Edit commentary for ${block.title}`} disabled={!hybridBlockHasEditableCommentary(block) || !!busy} onClick={(event) => { event.stopPropagation(); setSelectedId(block.id); window.setTimeout(() => window.document.getElementById('fg-hybrid-commentary-editor')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 0) }}><MessageSquareText size={12}/></button>
            <button title={block.kind === 'cover_toc' ? 'The report cover is a single required block' : 'Duplicate block'} aria-label={`Duplicate ${block.title}`} disabled={block.kind === 'cover_toc' || !!busy} onClick={(event) => { event.stopPropagation(); duplicateBlock(block) }}><Copy size={12}/></button>
            <button title={block.kind === 'cover_toc' ? 'The report cover is required' : 'Delete block'} aria-label={`Delete ${block.title}`} disabled={block.kind === 'cover_toc' || !!busy} onClick={(event) => { event.stopPropagation(); removeBlock(block) }}><Trash2 size={12}/></button>
          </div>
        </div>)}
        <details className="fg-hybrid-library"><summary><Plus size={14}/> Add governed block</summary>
          <p>Choose a specific governed online visual or saved view. Only blocks with data for this workbook and reporting month are shown.</p>
          {Array.from(libraryGroups.entries()).map(([category, items]) => <section key={category}>
            <h3>{category}</h3>
            {items.map((item) => <button key={item.library_id} onClick={() => addLibraryBlock(item)} disabled={!!busy} title={item.description || item.title}><strong>{item.title}</strong><span className="fg-hybrid-library-source">{libraryChoiceSourceLabel(item, live, draft.spec)}</span>{item.description ? <span>{item.description}</span> : null}</button>)}
          </section>)}
        </details>
      </aside>
      <section className="fg-hybrid-report-preview"><div className="fg-hybrid-preview-meta">Letter preview · {previewPages || '…'} page{previewPages === 1 ? '' : 's'} · exact export HTML · {previewHash.slice(0, 12)}</div><iframe title="Exact report preview" srcDoc={previewHtml} style={{ height: `${previewHeight}px` }} onLoad={(event) => bindPreviewFrame(event.currentTarget)}/></section>
      <aside className="fg-hybrid-report-inspector">
        <h2>Block inspector</h2>
        {coverBlock ? <details className="fg-hybrid-footer-controls">
          <summary>Report footer</summary>
          <p>These settings apply to every report body page. Leave the label blank to remove “Confidential”; add a disclaimer only when each page needs it.</p>
          <label>Footer label<input key={`footer-text:${draft.revision}`} defaultValue={reportFooterText} placeholder="Confidential" onBlur={(event) => { if (event.target.value !== reportFooterText) patch({ op: 'update', block_id: coverBlock.id, changes: { configuration: { ...coverBlock.configuration, footerText: event.target.value } } }, 'Updated the report footer label') }}/></label>
          <label>Footer disclaimer (optional)<textarea key={`footer-disclaimer:${draft.revision}`} defaultValue={reportFooterDisclaimer} placeholder="Optional text shown above the footer line on every body page" onBlur={(event) => { if (event.target.value !== reportFooterDisclaimer) patch({ op: 'update', block_id: coverBlock.id, changes: { configuration: { ...coverBlock.configuration, footerDisclaimer: event.target.value } } }, 'Updated the report footer disclaimer') }}/></label>
        </details> : null}
        {selected ? <>
          {selected.kind === 'cover_toc' ? <p className="fg-hybrid-cover-note">The cover is the required page-one disclosure for reporting month, actual/forecast scope, source freshness, and hybrid composition. Edit those fields in Excel Report settings.</p> : null}
          <label>Title<input aria-label="Block title" key={`${selected.id}:title:${draft.revision}`} defaultValue={selected.title} disabled={selected.kind === 'cover_toc'} onBlur={(event) => { if (event.target.value !== selected.title) patch({ op: 'update', block_id: selected.id, changes: { title: event.target.value } }, `Renamed ${selected.title}`) }}/></label>
          <label>Width<select value={selected.layout?.span || 12} disabled={selected.kind === 'cover_toc'} onChange={(event) => patch({ op: 'update', block_id: selected.id, changes: { layout: { ...selected.layout, span: Number(event.target.value) } } }, `Changed ${selected.title} width`)}><option value={12}>Full width</option><option value={6}>Half width</option></select></label>
          <label>Density<select value={selected.layout?.density || 'standard'} disabled={selected.kind === 'cover_toc'} onChange={(event) => patch({ op: 'update', block_id: selected.id, changes: { layout: { ...selected.layout, density: event.target.value } } }, `Changed ${selected.title} density`)}><option>compact</option><option>standard</option><option>detailed</option></select></label>
          <label>Emphasis<select value={selected.appearance?.tone || 'default'} disabled={selected.kind === 'cover_toc'} onChange={(event) => patch({ op: 'update', block_id: selected.id, changes: { appearance: { ...selected.appearance, tone: event.target.value } } }, `Formatted ${selected.title}`)}><option value="default">Default</option><option value="subtle">Subtle panel</option><option value="highlight">Highlighted</option></select></label>
          <label>Title alignment<select value={selected.appearance?.titleAlignment || 'left'} disabled={selected.kind === 'cover_toc'} onChange={(event) => patch({ op: 'update', block_id: selected.id, changes: { appearance: { ...selected.appearance, titleAlignment: event.target.value } } }, `Formatted ${selected.title} title`)}><option value="left">Left</option><option value="center">Centered</option></select></label>
          <label className="check"><input type="checkbox" checked={selected.enabled !== false} disabled={selected.kind === 'cover_toc'} onChange={(event) => patch({ op: 'update', block_id: selected.id, changes: { enabled: event.target.checked } }, `${event.target.checked ? 'Showed' : 'Hid'} ${selected.title}`)}/> Visible{selected.kind === 'cover_toc' ? ' · required report disclosure' : ''}</label>
          <label className="check"><input type="checkbox" checked={selected.layout?.keepTogether !== false} disabled={selected.kind === 'cover_toc'} onChange={(event) => patch({ op: 'update', block_id: selected.id, changes: { layout: { ...selected.layout, keepTogether: event.target.checked } } }, `Changed keep-together for ${selected.title}`)}/> Keep block together</label>
          <label className="check"><input type="checkbox" checked={selected.layout?.pageBreakBefore === true} disabled={selected.kind === 'cover_toc'} onChange={(event) => patch({ op: 'update', block_id: selected.id, changes: { layout: { ...selected.layout, pageBreakBefore: event.target.checked } } }, `Changed page break for ${selected.title}`)}/> Page break before</label>
          <label className="check"><input type="checkbox" checked={selected.appearance?.showSourceLabel === true} disabled={selected.kind === 'cover_toc'} onChange={(event) => patch({ op: 'update', block_id: selected.id, changes: { appearance: { ...selected.appearance, showSourceLabel: event.target.checked } } }, `Changed source label for ${selected.title}`)}/> Show source label</label>
          {(Array.isArray(selected.content?.matrix) || Array.isArray(selected.content?.snapshot_section?.matrix)) && <>
            <label>Maximum data rows<input key={`${selected.id}:maxRows:${draft.revision}`} type="number" min={5} max={100} placeholder="All rows" defaultValue={selected.configuration?.maxRows ?? ''} onBlur={(event) => { const raw = event.target.value; const value = raw === '' ? null : Math.max(5, Math.min(100, Number(raw) || 5)); if (value !== (selected.configuration?.maxRows ?? null)) patch({ op: 'update', block_id: selected.id, changes: { configuration: { ...selected.configuration, maxRows: value } } }, `Changed row limit for ${selected.title}`) }}/></label>
            <label className="check"><input type="checkbox" checked={selected.configuration?.showTableHeader !== false} onChange={(event) => patch({ op: 'update', block_id: selected.id, changes: { configuration: { ...selected.configuration, showTableHeader: event.target.checked } } }, `Changed table header for ${selected.title}`)}/> Show table header</label>
          </>}
          {(Array.isArray(selected.content?.cards) || Array.isArray(selected.content?.snapshot_section?.cards)) && <label>Maximum cards<select value={selected.configuration?.maxCards || 5} onChange={(event) => patch({ op: 'update', block_id: selected.id, changes: { configuration: { ...selected.configuration, maxCards: Number(event.target.value) } } }, `Changed card count for ${selected.title}`)}>{[1, 2, 3, 4, 5].map((count) => <option key={count} value={count}>{count}</option>)}</select></label>}
          {selectedChart && <label>Chart style<select value={selected.configuration?.chartStyle || 'auto'} onChange={(event) => patch({ op: 'update', block_id: selected.id, changes: { configuration: { ...selected.configuration, chartStyle: event.target.value } } }, `Changed chart style for ${selected.title}`)}><option value="auto">Source default</option><option value="bars">Bars</option>{selectedChartSupportsWaterfall ? <option value="waterfall">Waterfall</option> : null}</select>{!selectedChartSupportsWaterfall ? <small>Waterfall is available for a single governed series or an existing bridge.</small> : null}</label>}
          {selectedCanEditCommentary ? <section className="fg-hybrid-commentary-editor" id="fg-hybrid-commentary-editor">
            <h3><MessageSquareText size={15}/> Commentary</h3>
            <p>Click commentary in the preview to select its block. Edit it here, or give AI a specific rewrite instruction.</p>
            <label>Commentary<textarea ref={commentaryEditorRef} key={`${selected.id}:commentary:${draft.revision}`} defaultValue={selectedCommentary?.body || ''}/></label>
            <div className="fg-hybrid-commentary-actions">
              <button className="primary" disabled={!!busy} onClick={() => saveSelectedCommentary(commentaryEditorRef.current?.value || '', `Edited commentary for ${selected.title}`)}>Save commentary</button>
              {selectedCommentary?.body && selectedCommentary.describesCurrentContext === false ? <button disabled={!!busy} onClick={() => saveSelectedCommentary(selectedCommentary.body, `Accepted commentary for the current ${selected.title} capture`)}><CheckCircle2 size={13}/> Use with current data</button> : null}
            </div>
            {selectedCommentary?.body ? <p className={selectedCommentary.describesCurrentContext === false ? 'fg-hybrid-commentary-stale' : 'fg-hybrid-commentary-current'}>{selectedCommentary.describesCurrentContext === false ? 'This text predates this block’s current source capture. Edit it or explicitly accept it for the current data.' : 'This commentary is accepted for this block’s current source capture.'}</p> : null}
            <label>AI rewrite instruction<textarea className="fg-hybrid-commentary-prompt" value={commentaryPrompt} onChange={(event) => setCommentaryPrompt(event.target.value)} placeholder="For example: Make this concise, lead with cash and margin, and end with the decision required."/></label>
            <button disabled={!!busy || !commentaryPrompt.trim()} onClick={() => applyAiEdit(`Rewrite only the commentary for report block \"${selected.title}\". Preserve supported facts and governed values. ${commentaryPrompt.trim()}`, selected.id, true)}><Sparkles size={14}/> Rewrite this commentary with AI</button>
          </section> : null}
          <dl><dt>Origin</dt><dd>{blockOriginLabel(selected)}</dd><dt>Source</dt><dd>{blockSourceLabel(selected)}</dd><dt>Reporting month</dt><dd>{monthName(reportMonth)}</dd><dt>Preview placement</dt><dd>{selectedPreviewPages.length ? `${selectedPreviewPages.length === 1 ? 'Page' : 'Pages'} ${selectedPreviewPages.join(', ')}` : selected.enabled === false ? 'Hidden from preview' : 'Calculating…'}</dd><dt>Pagination</dt><dd>{selectedPreviewPages.length > 1 ? `Automatically split because ${selectedDataRowCount || 'the content'} exceeds the ${selectedRowBudget || 'readable'}-row ${selected.layout?.density || 'standard'} density target` : selected.layout?.pageBreakBefore ? `Starts on a new page${selectedRowBudget ? ` · fits ${selectedDataRowCount} of ${selectedRowBudget} readable rows` : ''}` : selectedRowBudget ? `Fits on one page · ${selectedDataRowCount} of ${selectedRowBudget} readable rows at ${selected.layout?.density || 'standard'} density` : 'Fits on one page'}</dd><dt>Freshness</dt><dd>{selected.freshness?.stale ? 'Captured values are stale' : selected.freshness?.capturedAt || 'Current capture'}{selected.origin.startsWith('excel_') && !draft.excel_connected ? ' · Excel auto-sync paused' : ''}</dd><dt>Source hash</dt><dd>{selected.freshness?.sourceHash?.slice(0, 16) || 'Not supplied'}</dd><dt>Block ID</dt><dd>{selected.id}</dd></dl>
          <div className="fg-hybrid-inspector-actions"><button disabled={selected.kind === 'cover_toc' || !!busy} onClick={() => duplicateBlock(selected)}><Copy size={14}/> Duplicate</button><button className="danger" disabled={selected.kind === 'cover_toc' || !!busy} onClick={() => removeBlock(selected)}><Trash2 size={14}/> Delete</button></div>
        </> : <p>Select a block to edit it.</p>}
        <div className="fg-hybrid-ai"><h2><Sparkles size={16}/> Refine report with AI</h2><textarea value={aiPrompt} onChange={(event) => setAiPrompt(event.target.value)} placeholder="Make the report more concise, add a cash-flow trend, and move actions to the end."/><button className="primary" onClick={() => applyAiEdit()} disabled={!!busy || !aiPrompt.trim()}>Apply AI edit</button><p>Use the commentary editor above for a targeted rewrite. All AI changes are governed, revisioned, and undoable; source values and identities stay locked.</p></div>
      </aside>
    </div>
    <footer className="fg-hybrid-report-footer">{message}</footer>
  </main>
}
